import { useAuth } from "@/hooks/useAuth"
import { usePhotoMutations } from "@/hooks/usePhotoMutations"
import { usePhotosForEntity } from "@/hooks/usePhotosForEntity"
import { useSurveySite } from "@/hooks/useSurveySite"
import { useUpdateSurveySite } from "@/hooks/useUpdateSurveySite"
import { useDeleteSurveySite } from "@/hooks/useDeleteSurveySite"
import {
  getFormFieldComponent,
  getSchema,
  SiteFormDataInput,
  type SiteFormContext,
  transformSurveySiteData,
} from "@fieldbook/common/schemas/sites"
import { RecordersSection } from "@fieldbook/common/components"
import { useNvisSpeciesResolver } from "@fieldbook/common/hooks"
import { isSurveySite } from "@fieldbook/common/types/surveySites"
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useForm, type FieldErrors } from "react-hook-form"
import * as z from "zod"
import {
  useDraftRestore,
  useDraftRestoredNotice,
  useFormDraft,
} from "@/hooks/useFormDraft"
import { createDiscardCoordinator } from "@/lib/drafts/discardCoordinator"
import { draftPhotoToStaged } from "@/lib/drafts/draftPhotos"
import { buildResumeKey, type FormDraft } from "@/lib/drafts/types"
import { useSuppressProjectMap } from "@/contexts/ProjectMapVisibility"
import { getLocationError } from "@/lib/siteForm/errors"
import { captureFormSubmitBlocked } from "@/lib/siteForm/analytics"
import { useHasUnsavedChanges } from "@/hooks/useHasUnsavedChanges"
import { saveDraftForExit } from "@/lib/drafts/saveDraftForExit"

import { NvisDescriptionEditor } from "@fieldbook/common/components"
import { parseNvisDescriptionValue } from "@fieldbook/common/schemas/nvis"
import {
  buildPickerOptionsFromObservations,
  extractNvisObservations,
  stripNvisDescriptionNames,
} from "@fieldbook/common/utils/nvis"
import type { ResolvedNvisDescription } from "@fieldbook/common/types"
import { useObservationsWithDetails } from "@/components/project/ProjectDataSheet/Observations/useObservationsWithDetails"
import { Spinner } from "@fieldbook/ui/spinner"

import {
  LocationSelector,
  LocationSelectorProps,
  LocationShapes,
} from "@/components/locations/LocationSelector"
import { PhotosForm } from "@/components/photos/PhotosForm"
import { FormExitGuard } from "@/components/common/FormExitGuard"

import {
  PhotoChangesProvider,
  usePhotoChanges,
} from "@/components/photos/PhotosFormProvider"
import { useProjectData } from "@/contexts/ProjectData"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@fieldbook/ui/alert-dialog"
import { Button } from "@fieldbook/ui/button"
import { Input } from "@fieldbook/ui/input"
import { Label } from "@fieldbook/ui/label"
import { Textarea } from "@fieldbook/ui/textarea"
import { MapPin, Trash2 } from "lucide-react"
import { prepareSiteDefaultValues } from "./data/sites/-utils"

const editSearchSchema = z.object({
  /**
   * When set, the route swaps the full site edit form for a standalone
   * NVIS autogenerate flow that targets the named custom field. The user
   * lands directly in the description modal — no surrounding site form to
   * navigate — and Done/Cancel both navigate back to the site detail page.
   */
  autoGenerateNvis: z.string().optional(),
  draftId: z.string().uuid().optional(),
})

// Deliberately a sibling of `data`, not a child of it. This form renders as a
// full-screen overlay (`absolute inset-0 z-20`, same as `sites.new`). Nested
// under `data`, that overlay resolved against the data sheet's positioning
// context and so filled the 540px sheet, while the form's *viewport*
// breakpoints (`md:columns-2 lg:columns-4`) still laid out for the whole
// window — four columns crammed into half a screen on a medium display.
export const Route = createFileRoute(
  "/_private/projects/$id/sites/$siteId/edit",
)({
  validateSearch: (search) => editSearchSchema.parse(search),
  // Component dispatch lives inline in the route config: a single URL with
  // two distinct UI modes selected by search params. This keeps the
  // autogenerate workflow self-contained without pulling its concerns into
  // the form pipeline or `SiteFormContext`.
  component: function EditRoute() {
    const { autoGenerateNvis } = Route.useSearch()
    if (autoGenerateNvis) {
      return <GenerateNvis field={autoGenerateNvis} />
    }
    return <EditSiteWrapper />
  },
})

/** This form keeps nothing outside react-hook-form worth restoring. */
type SiteDraft = FormDraft<SiteFormDataInput, null>

function EditSiteWrapper() {
  // This route covers the project map for its whole life, loading states
  // included. Releasing the map's WebGL context and tile buffers here keeps
  // the process small enough to survive being backgrounded behind the camera.
  useSuppressProjectMap()

  const { id: projectId, siteId } = Route.useParams()
  const { user } = useAuth()
  const { draftId } = Route.useSearch()
  const { data: existingPhotos, isLoading: photosLoading } = usePhotosForEntity(
    {
      entityId: siteId,
      entityType: "survey_site",
    },
  )

  const resumeKey = buildResumeKey({
    kind: "site",
    projectId,
    mode: "edit",
    entityId: siteId,
  })
  const restore = useDraftRestore<SiteFormDataInput, null>(
    resumeKey,
    user?.id ?? null,
    draftId,
  )

  // Wait for photos and any saved draft before rendering the provider:
  // react-hook-form freezes its defaults at mount.
  if (photosLoading || restore.status === "pending") {
    return (
      <div className="bg-background absolute inset-0 z-20 flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    )
  }

  return (
    <PhotoChangesProvider
      initialPhotos={existingPhotos ?? []}
      restoredKeep={restore.draft?.existingPhotos ?? undefined}
      initialStagedPhotos={restore.draft?.photos.map(draftPhotoToStaged)}
    >
      <EditSite
        resumeKey={resumeKey}
        restoredDraft={restore.draft}
        openedFromDraft={Boolean(draftId && restore.draft?.savedByUser)}
      />
    </PhotoChangesProvider>
  )
}

function EditSite({
  resumeKey,
  restoredDraft,
  openedFromDraft,
}: {
  resumeKey: string
  restoredDraft: SiteDraft | null
  openedFromDraft: boolean
}) {
  const { id: projectId, siteId } = Route.useParams()

  const {
    data: { project, observations, speciesRecorded, collections },
  } = useProjectData()
  const { user, org } = useAuth()
  const navigate = useNavigate()

  // Fetch actual site data
  const { data: site, isLoading: siteLoading } = useSurveySite({
    siteId,
    projectId,
  })

  const updateSurveySite = useUpdateSurveySite()
  const deleteSurveySite = useDeleteSurveySite()
  const photoMutations = usePhotoMutations(siteId, "survey_site", org?.id ?? "")

  const [photoChanges] = usePhotoChanges()

  // Find the site type from the actual site data
  const siteTypeName = site?.type
  const siteType = project?.site_types?.find((st) => st.name === siteTypeName)
  const siteLocationType =
    (site?.location_type?.toLowerCase() as SiteFormDataInput["location_type"]) ??
    "point"

  const schema = useMemo(() => getSchema(siteType), [siteType])

  const siteObservations = useMemo(
    () => (observations ?? []).filter((o) => o.survey_site_id === siteId),
    [observations, siteId],
  )
  // Pre-loaded species + collections from PowerSync (local SQLite). The
  // resolver is now a pure transform — it does no fetching of its own — so
  // the PWA can work entirely offline against the cached rows.
  const { resolveDescription, resolveObservationInputs } =
    useNvisSpeciesResolver({
      species: speciesRecorded ?? [],
      collections: collections ?? [],
    })
  const siteContext = useMemo<SiteFormContext>(
    () => ({
      observations: siteObservations,
      observationFieldDefs: project?.observation_custom_fields ?? [],
      resolveNvisDescription: resolveDescription,
      resolveNvisObservationInputs: resolveObservationInputs,
    }),
    [
      siteObservations,
      project?.observation_custom_fields,
      resolveDescription,
      resolveObservationInputs,
    ],
  )

  const location_property =
    `location_${siteLocationType}` as keyof SiteFormDataInput

  const defaultValues = useMemo(
    () => prepareSiteDefaultValues(site as Record<string, unknown> | null),
    [site],
  )

  // Typed as partial on purpose: a draft written before a custom field was
  // added has no value for it, and the computed defaults below must still
  // supply one rather than being blanked out by an absent key.
  const restoredValues: Partial<SiteFormDataInput> = restoredDraft?.values ?? {}
  // The reset effect below runs after the site row arrives; it reads the
  // restored values through a ref so it isn't re-triggered by their identity.
  const restoredValuesRef = useRef(restoredValues)

  const form = useForm<SiteFormDataInput>({
    defaultValues: {
      location_type: siteLocationType,
      project_id: projectId,
      created_by: site?.created_by ?? user?.id ?? "No user",
      type: siteType?.name ?? `Unknown site`,
      ...defaultValues,
      ...restoredValues,
    },
    resolver: standardSchemaResolver(schema),
    mode: "all",
  })

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors, dirtyFields },
    control,
    watch,
    setValue,
    reset,
  } = form

  // Track the last site+siteType combo we've reset for, so we don't loop.
  // PowerSync's reactive queries return a new object reference on every update
  // even when data hasn't changed, so we can't use `site` directly as a dep.
  const lastResetKeyRef = useRef<string | null>(null)

  // Reset form when site data loads (or when route params change)
  useEffect(() => {
    if (site && siteType) {
      const resetKey = `${site.id}:${siteType.name}:${siteLocationType}:${projectId}`
      if (lastResetKeyRef.current === resetKey) return
      lastResetKeyRef.current = resetKey

      const prepared = prepareSiteDefaultValues(
        site as Record<string, unknown> | null,
      )
      reset({
        location_type: siteLocationType,
        project_id: projectId,
        created_by: site.created_by,
        type: siteType.name,
        ...prepared,
        // Last, so the site row arriving after mount doesn't overwrite edits
        // the user had already made and we just restored.
        ...restoredValuesRef.current,
      } as SiteFormDataInput)
    }
  }, [site, siteType, siteLocationType, projectId, reset])

  const [descriptionFocus, setDescriptionFocus] = useState(false)
  const [showLocationSelector, setShowLocationSelector] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const draft = useFormDraft({
    resumeKey,
    kind: "site",
    projectId,
    ownerId: user?.id ?? null,
    entityId: siteId,
    label: watch("name")?.trim() || "Unnamed survey site",
    form,
    extras: null,
    photos: photoChanges.add,
    existingPhotos: photoChanges.keep,
    restoredDraft,
    // Autosaving over a form that is already committing would race the discard
    // that follows a successful save.
    enabled: Boolean(user) && !isSubmitting,
  })

  useDraftRestoredNotice(restoredDraft, "site")

  const hasUnsavedChanges = useHasUnsavedChanges({
    form,
  })

  // Leaving only: FormExitGuard discards the draft first.
  const handleCancel = useCallback(() => {
    navigate({
      to: "/projects/$id/data/sites/$siteId",
      params: { id: projectId, siteId },
    })
  }, [navigate, projectId, siteId])

  const handleSaveDraft = useCallback(
    () =>
      saveDraftForExit({
        validateIdentifier: () => form.trigger("name"),
        save: draft.saveForExit,
      }),
    [form, draft],
  )

  const onSubmit = useCallback(
    async (data: SiteFormDataInput) => {
      if (!user || !org) throw new Error("Not logged in")
      if (!projectId) throw new Error("Project ID is required")
      if (!siteType) throw new Error("Site type not found")

      // Only send custom fields that the user actually changed,
      // to avoid overwriting untouched fields with Zod-preprocessed defaults
      if (dirtyFields.custom_fields) {
        // Build an object with existing values, then overlay only dirty fields
        const existingCustomFields =
          site?.custom_fields?.reduce<Record<string, unknown>>(
            (acc, { name, value }) => {
              acc[name] = value
              return acc
            },
            {},
          ) ?? {}

        const dirtyOnlyCustomFields = Object.fromEntries(
          Object.entries(data.custom_fields).filter(
            ([key]) =>
              dirtyFields.custom_fields?.[
                key as keyof typeof dirtyFields.custom_fields
              ],
          ),
        )

        data = {
          ...data,
          custom_fields: {
            ...existingCustomFields,
            ...dirtyOnlyCustomFields,
          } as typeof data.custom_fields,
        }
      } else {
        // No custom fields were touched — preserve existing values as-is
        const existingCustomFields =
          site?.custom_fields?.reduce<Record<string, unknown>>(
            (acc, { name, value }) => {
              acc[name] = value
              return acc
            },
            {},
          ) ?? {}

        data = {
          ...data,
          custom_fields: existingCustomFields as typeof data.custom_fields,
        }
      }

      const transformed = transformSurveySiteData(data)
      const updateData = {
        id: siteId,
        ...transformed,
      }

      // Validate the transformed data
      if (!isSurveySite(updateData)) {
        console.error("Invalid survey site data:", updateData)
        return
      }

      // Fire mutations (non-blocking, may pause if offline)
      const discard = createDiscardCoordinator(
        ["entity", "photos"],
        draft.discard,
      )
      // Settled through the promise: mutate's per-call callbacks are dropped
      // once navigation unmounts this form.
      void updateSurveySite.mutateAsync(updateData).then(
        () => discard.succeeded("entity"),
        (error: unknown) => {
          console.error(error)
          discard.settled("entity", false)
        },
      )
      photoMutations.applyChanges(photoChanges, {
        onAllSettled: (allSucceeded) => discard.settled("photos", allSucceeded),
      })

      // Navigate immediately (optimistic)
      navigate({
        to: "/projects/$id/data/sites/$siteId",
        params: { id: projectId, siteId },
      })
    },
    [
      user,
      org,
      projectId,
      siteType,
      siteId,
      site,
      dirtyFields,
      updateSurveySite,
      photoChanges,
      photoMutations,
      navigate,
      draft.discard,
    ],
  )

  // Validation-blocked submit (as opposed to a thrown error in onSubmit):
  // capture the exact failing fields at the moment the user taps Save. The
  // Save button is enabled regardless of validity so this fires — a
  // disabled button would hide both the capture and the reason from the
  // user.
  const onInvalid = useCallback((errors: FieldErrors<SiteFormDataInput>) => {
    captureFormSubmitBlocked("edit_site", errors)
  }, [])

  const handleDelete = useCallback(async () => {
    setIsDeleting(true)
    try {
      await deleteSurveySite.mutateAsync({ id: siteId, project_id: projectId })
      // Nothing left to restore onto.
      await draft.discard()
      navigate({ to: "/projects/$id", params: { id: projectId } })
    } catch (error) {
      console.error("Failed to delete site:", error)
    } finally {
      setIsDeleting(false)
    }
  }, [siteId, projectId, navigate, deleteSurveySite, draft])

  const isGeometryValid = useCallback(
    (geometry: LocationShapes | undefined) => {
      if (!geometry) return true
      const parsed = schema.safeParse({
        location_type: siteLocationType,
        [location_property]: geometry,
      })
      if (parsed.success) return true
      return !(location_property in (parsed.error.flatten()?.fieldErrors ?? {}))
    },
    [location_property, siteLocationType, schema],
  )

  // Show whatever location error exists — the schema can fail on a
  // different location shape than the one being edited (see
  // getLocationError), so fall back across all of them.
  const locationError = getLocationError(errors, location_property)

  if (!user) return

  if (siteLoading) {
    return (
      <div className="bg-background absolute inset-0 z-20 flex items-center justify-center">
        <div className="text-lg">Loading site...</div>
      </div>
    )
  }

  if (!site) {
    return (
      <div className="bg-background absolute inset-0 z-20 p-4">
        <div className="text-center">
          <h1 className="mb-2 text-xl font-bold text-red-600">
            Site Not Found
          </h1>
          <p className="mb-4 text-gray-600">
            The requested site could not be found.
          </p>
          <Button
            onClick={() =>
              navigate({ to: "/projects/$id", params: { id: projectId } })
            }
          >
            Back to Project
          </Button>
        </div>
      </div>
    )
  }

  if (!siteType) {
    return (
      <div className="bg-background absolute inset-0 z-20 p-4">
        <div className="text-center">
          <h1 className="mb-2 text-xl font-bold text-red-600">
            Site Type Not Found
          </h1>
          <p className="mb-4 text-gray-600">
            The site type "{siteTypeName}" is not available for this project.
          </p>
          <Button
            onClick={() =>
              navigate({ to: "/projects/$id", params: { id: projectId } })
            }
          >
            Back to Project
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-background absolute inset-0 z-20 mb-2 flex flex-col justify-between gap-2">
      <div className="space-y-2 overflow-y-scroll">
        <div className="flex items-center p-2 text-2xl">
          <MapPin className="mr-2 size-6" />
          Edit {siteType.label}
        </div>

        <div className="flex flex-col gap-4 px-1">
          {/* Basic Fields */}
          <div className="space-y-1">
            <Label htmlFor="name">Site Name *</Label>
            <Input
              {...register("name")}
              id="name"
              className="h-12 text-lg"
              autoComplete="off"
              placeholder={`Enter ${siteType.label.toLowerCase()} name`}
            />
            {errors.name && (
              <div className="mt-1 text-sm text-red-600">
                {errors.name.message}
              </div>
            )}
          </div>

          {/* Location */}
          <div className="space-y-1">
            <Label htmlFor="location">
              Location ({siteType.site_location_type})
            </Label>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowLocationSelector(true)}
              className="h-12 w-full justify-start text-lg"
            >
              <MapPin className="mr-2 size-5" />
              {watch(`location_${siteLocationType}`)
                ? "Location set - tap to edit"
                : `Select ${siteLocationType} location`}
            </Button>
            {locationError && (
              <div className="mt-1 text-sm text-red-600">{locationError}</div>
            )}
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              {...register("description")}
              id="description"
              className={`h-20 text-lg transition-all duration-500 ease-in-out ${
                descriptionFocus ? "h-40" : ""
              }`}
              placeholder="Enter site description or notes"
              onFocus={() => setDescriptionFocus(true)}
              onBlur={() => setDescriptionFocus(false)}
            />
          </div>

          {/* Recorders */}
          <RecordersSection control={control} user={user} />

          {siteType.custom_fields.length > 0 && (
            <>
              <h3 className="text-lg font-semibold">Site Data</h3>
              <div className="space-y-2 md:columns-2 lg:columns-4">
                {siteType.custom_fields.map((field) => (
                  <div
                    key={`customFields.${field.name}`}
                    className="rounded-lg"
                  >
                    <div className="mb-1 flex flex-col items-center gap-2">
                      {getFormFieldComponent(
                        field,
                        register,
                        control,
                        siteContext,
                      )}
                      {field.type !== "subform" &&
                        errors.custom_fields?.[field.name]?.message && (
                          <span className="self-end text-orange-400">
                            {String(
                              errors.custom_fields?.[field.name]?.message,
                            )}
                          </span>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Photos */}
          <PhotosForm
            entityType="survey_site"
            onCaptureStateChange={draft.setCaptureInFlight}
          />

          {/* Delete Section */}
          <div className="rounded-lg border border-red-200 bg-red-50 p-3">
            <h3 className="mb-2 font-medium text-red-900">Danger Zone</h3>
            <p className="mb-3 text-sm text-red-700">
              Permanently delete this site and all associated data. This action
              cannot be undone.
            </p>
            <div className="flex w-full justify-end">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    type="button"
                    disabled={isDeleting}
                    variant="outline"
                    className="border-red-300 text-red-600 hover:bg-red-100"
                  >
                    <Trash2 className="mr-2 size-4" />
                    {isDeleting ? "Deleting..." : "Delete Site"}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Site</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete this site? This action
                      cannot be undone and will permanently remove the site and
                      all associated data.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDelete}
                      className="bg-red-600 text-white hover:bg-red-700"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col gap-2 border-t border-green-800 px-1 pt-2 md:flex-row md:gap-4">
        <FormExitGuard
          hasUnsavedChanges={hasUnsavedChanges}
          discardDraft={draft.discard}
          onCancel={handleCancel}
          onSaveDraft={handleSaveDraft}
          isEditingDraft={openedFromDraft}
        />
        <Button
          type="submit"
          disabled={isSubmitting}
          className="h-12 w-full text-lg"
          onClick={handleSubmit(onSubmit, onInvalid)}
        >
          {isSubmitting ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      {/* Location Selector Overlay */}
      {showLocationSelector && project && (
        <LocationSelector
          {...({
            isOpen: showLocationSelector,
            onClose: () => setShowLocationSelector(false),
            locationType: siteLocationType,
            project,
            value: watch(`location_${siteLocationType}`),
            onChange: (geometry: LocationShapes | undefined) =>
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              setValue(`location_${siteLocationType}`, geometry as any, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
              }),
            isValid: isGeometryValid,
          } as LocationSelectorProps)}
        />
      )}
    </div>
  )
}

/**
 * Standalone autogenerate-NVIS-description workflow. Selected by the route
 * dispatch above when `?autoGenerateNvis=<field>` is present.
 *
 * Renders only the {@link NvisDescriptionEditor} dialog (no surrounding site
 * form), reads the named NVIS custom field, auto-runs the deterministic
 * builder, and on Done patches `custom_fields` on the site directly via the
 * existing PowerSync mutation. Both Done and Cancel navigate straight back
 * to the site detail page — the user never sees the full edit form.
 */
function GenerateNvis({ field: fieldName }: { field: string }) {
  const { id: projectId, siteId } = Route.useParams()
  const navigate = useNavigate()

  const { data: site, isLoading: siteLoading } = useSurveySite({
    siteId,
    projectId,
  })
  const {
    data: { project, speciesRecorded, collections },
  } = useProjectData()
  const projectObservations = useObservationsWithDetails()
  const siteObservations = useMemo(
    () => projectObservations?.filter((o) => o.survey_site_id === siteId) ?? [],
    [projectObservations, siteId],
  )
  const updateSurveySite = useUpdateSurveySite()

  const siteType = project?.site_types?.find((t) => t.name === site?.type)
  const fieldDef = siteType?.custom_fields.find((f) => f.name === fieldName)

  const { resolveDescription, resolveObservationInputs } =
    useNvisSpeciesResolver({
      species: speciesRecorded ?? [],
      collections: collections ?? [],
    })

  const observationInputs = useMemo(() => {
    if (!fieldDef || fieldDef.type !== "nvis_vegetation_description") {
      return undefined
    }
    const raw = extractNvisObservations(
      siteObservations,
      fieldDef.nvis_field_mapping,
      project?.observation_custom_fields ?? [],
    )
    return resolveObservationInputs(raw)
  }, [
    siteObservations,
    fieldDef,
    project?.observation_custom_fields,
    resolveObservationInputs,
  ])

  // Picker entries: one per unique (taxon × growth form) from this site's
  // observations. See `buildPickerOptionsFromObservations` for the rules.
  const availableSpecies = useMemo(
    () =>
      observationInputs
        ? buildPickerOptionsFromObservations(observationInputs)
        : [],
    [observationInputs],
  )

  // Lazy initial state captures the saved description (if any). Site data is
  // loaded by the time we render past the spinner guard, so this fires once.
  const [value, setValue] = useState<ResolvedNvisDescription | null>(() => {
    const existing = site?.custom_fields?.find(
      (f) => f.name === fieldName,
    )?.value
    if (!existing || typeof existing !== "string") return null
    const parsed = parseNvisDescriptionValue(existing)
    return parsed ? resolveDescription(parsed) : null
  })

  const navigateBack = useCallback(() => {
    navigate({
      to: "/projects/$id/data/sites/$siteId",
      params: { id: projectId, siteId },
    })
  }, [navigate, projectId, siteId])

  const handleSave = useCallback(async () => {
    if (!site) {
      navigateBack()
      return
    }
    // Build the next custom_fields array: drop any existing entry for this
    // field, append the new JSON-encoded value. Empty editor state writes
    // `null` so the field is cleared cleanly.
    const stripped = value ? stripNvisDescriptionNames(value) : null
    const existing = site.custom_fields ?? []
    const without = existing.filter((f) => f.name !== fieldName)
    const next = [
      ...without,
      { name: fieldName, value: stripped ? JSON.stringify(stripped) : null },
    ]
    try {
      await updateSurveySite.mutateAsync({
        id: siteId,
        project_id: projectId,
        custom_fields: next,
      })
    } finally {
      navigateBack()
    }
  }, [
    site,
    value,
    fieldName,
    updateSurveySite,
    siteId,
    projectId,
    navigateBack,
  ])

  // If the field doesn't exist on the site type or isn't an NVIS field, the
  // route was navigated to with bad params — bail back to the detail page.
  // Triggering navigation as a side effect keeps the render pure (no
  // navigate calls inside the render tree, which React would otherwise
  // warn about) and stays robust if the search param flips at runtime.
  const fieldInvalid =
    !siteLoading &&
    Boolean(site) &&
    (!fieldDef || fieldDef.type !== "nvis_vegetation_description")
  useEffect(() => {
    if (fieldInvalid) navigateBack()
  }, [fieldInvalid, navigateBack])

  if (siteLoading || !site || fieldInvalid) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner />
      </div>
    )
  }

  return (
    <NvisDescriptionEditor
      value={value}
      onChange={setValue}
      onSave={handleSave}
      onCancel={navigateBack}
      availableSpecies={availableSpecies}
      observationInputs={observationInputs}
      autoOpen
      autoGenerate
    />
  )
}
